import { string } from 'zod'

import { toSlug } from './slug'

export const PROJECT_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const PROJECT_SLUG_MIN_LENGTH = 3

export const PROJECT_SLUG_MAX_LENGTH = 64

export const RESERVED_PROJECT_SLUGS = new Set([
  'projects',
  'workspace',
  'management',
  'create',
  'edit',
  'requests',
  'admin',
  'about',
  'members',
  'templates',
])

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isUuid(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  return UUID_REGEX.test(value)
}

export function isProjectSlug(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed.length < PROJECT_SLUG_MIN_LENGTH || trimmed.length > PROJECT_SLUG_MAX_LENGTH) {
    return false
  }
  if (!PROJECT_SLUG_REGEX.test(trimmed)) {
    return false
  }
  if (RESERVED_PROJECT_SLUGS.has(trimmed)) {
    return false
  }
  return true
}

export interface ProjectRouteIdentity {
  id: string
  slug?: string | null
}

export function getProjectRouteParam(project: ProjectRouteIdentity): string {
  const slug = project.slug?.trim()
  if (slug) {
    return slug
  }
  return project.id
}

export interface ProjectQueryVariables {
  id?: string
  slug?: string
}

export function resolveProjectQueryVariables(ref: string): ProjectQueryVariables {
  if (isUuid(ref)) {
    return { id: ref }
  }
  return { slug: ref }
}

const CYRILLIC_REGEX = /[\u0430-\u044F\u0451]/i

export function hasCyrillicText(value: string): boolean {
  return CYRILLIC_REGEX.test(value)
}

export function normalizeProjectSlugInput(value: string): string {
  let slug = toSlug(value.trim())
  slug = slug.slice(0, PROJECT_SLUG_MAX_LENGTH).replace(/-+$/g, '')
  return slug
}

export function normalizeProjectSlugInputOnBlur(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  return normalizeProjectSlugInput(trimmed)
}

export function toProjectSlugFromName(name: string): string {
  let slug = normalizeProjectSlugInput(name)

  if (slug.length < PROJECT_SLUG_MIN_LENGTH) {
    slug = `project-${Date.now()}`
  }

  if (RESERVED_PROJECT_SLUGS.has(slug)) {
    slug = `${slug}-${Date.now()}`
  }

  if (slug.length > PROJECT_SLUG_MAX_LENGTH) {
    slug = slug.slice(0, PROJECT_SLUG_MAX_LENGTH).replace(/-+$/g, '')
  }

  return slug
}

export function appendProjectSlugSuffix(baseSlug: string, suffix: number | string): string {
  const suffixStr = String(suffix)
  const maxBaseLen = Math.max(1, PROJECT_SLUG_MAX_LENGTH - suffixStr.length - 1)
  const truncated = baseSlug.slice(0, maxBaseLen).replace(/-+$/g, '') || 'project'
  return `${truncated}-${suffixStr}`.slice(0, PROJECT_SLUG_MAX_LENGTH).replace(/-+$/g, '')
}

export function buildProjectPath(ref: string, suffix = ''): string {
  return `/projects/${ref}${suffix}`
}

export function replaceProjectRefInPath(path: string, nextRef: string): string {
  const prefix = '/projects/'
  if (!path.startsWith(prefix)) {
    return buildProjectPath(nextRef)
  }
  const withoutPrefix = path.slice(prefix.length)
  const slashIndex = withoutPrefix.indexOf('/')
  const suffix = slashIndex === -1 ? '' : withoutPrefix.slice(slashIndex)
  return buildProjectPath(nextRef, suffix)
}

export const projectSlugManualSchema = string()
  .trim()
  .superRefine((value, ctx) => {
    if (!value) {
      return
    }
    if (value.length < PROJECT_SLUG_MIN_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        message: 'Минимум 3 символа',
      })
      return
    }
    if (value.length > PROJECT_SLUG_MAX_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        message: 'Максимум 64 символа',
      })
      return
    }
    if (!PROJECT_SLUG_REGEX.test(value)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Только латиница, цифры и дефисы',
      })
      return
    }
    if (RESERVED_PROJECT_SLUGS.has(value)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Этот адрес зарезервирован',
      })
    }
  })
